import React, { useState, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, MicOff, Square, Send, User, Camera } from "lucide-react";
import { db } from "@/api/db";
import { INTERVIEWER_PROFILES } from "@/components/interview/StepConfig";

// ─── TTS via API (ElevenLabs key stays server-side) ──────────────────────────
async function fetchTTS(text, voiceId) {
  const { data } = await db.functions.invoke("textToSpeech", { text, voice_id: voiceId }, { responseType: "arraybuffer" });
  return data;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatTime(s) {
  return `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;
}

function cleanText(text) {
  return text.replace(/\*\*([^*]+)\*\*/g, "$1").replace(/\*([^*\n]+)\*/g, "").replace(/\s+/g, " ").trim();
}

function startWordTimer(wordCount, durationMs, onTick, onDone) {
  const ms = Math.max(60, durationMs / wordCount);
  let i = 0;
  const id = setInterval(() => { i++; onTick(i); if (i >= wordCount) { clearInterval(id); onDone(); } }, ms);
  return () => clearInterval(id);
}

// ─── Frame capture ────────────────────────────────────────────────────────────
function captureVideoFrame(videoEl) {
  if (!videoEl || !videoEl.videoWidth) return null;
  const canvas = document.createElement("canvas");
  canvas.width  = 320;
  canvas.height = 240;
  canvas.getContext("2d").drawImage(videoEl, 0, 0, 320, 240);
  // Return base64 without the data-URI prefix
  return canvas.toDataURL("image/jpeg", 0.65).split(",")[1];
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function InterviewSession({ session, onFinish }) {
  const safeTranscript = Array.isArray(session.transcript) ? session.transcript : [];
  const [transcript, setTranscript]             = useState(safeTranscript);
  const [isRecording, setIsRecording]           = useState(false);
  const [inputText, setInputText]               = useState("");
  const [elapsed, setElapsed]                   = useState(0);
  const [loading, setLoading]                   = useState(false);
  const [phase, setPhase]                       = useState("ready"); // "ready"|"loading"|"session"
  const [isPlayingAudio, setIsPlayingAudio]     = useState(false);
  const [errorMsg, setErrorMsg]                 = useState(null);
  const [subtitleWords, setSubtitleWords]       = useState([]);
  const [visibleWordCount, setVisibleWordCount] = useState(0);
  const [lastCandidateText, setLastCandidateText] = useState(null);
  const [audioWave, setAudioWave]               = useState([0,0,0,0,0,0,0]);
  const [videoGranted, setVideoGranted]         = useState(false);
  const [turnInfo, setTurnInfo]                 = useState(null); // { current, total }

  const timerRef        = useRef(null);
  const recognitionRef  = useRef(null);
  const currentSrcRef   = useRef(null);
  const ttsCtxRef       = useRef(null);
  const micCtxRef       = useRef(null);
  const analyserRef     = useRef(null);
  const micStreamRef    = useRef(null);
  const stopTimerRef    = useRef(null);
  const voiceDataRef    = useRef({ volumes: [], pauses: [], speechDuration: 0 });
  const silenceTimerRef      = useRef(null);  // auto-send after silence
  const latestTextRef        = useRef("");    // latest recognized text (avoids stale closure)
  const transcriptRef        = useRef(safeTranscript); // always-current transcript (avoids stale closure)
  const shouldBeRecordingRef = useRef(false); // true = mic should restart if Chrome kills it
  const finishedRef          = useRef(false); // true after "Encerrar" — blocks all further async work
  const playingRef           = useRef(false); // synchronous mutex — only one TTS playback at a time
  const sendingRef           = useRef(false); // synchronous mutex — only one sendMessage at a time
  // Video
  const videoRef        = useRef(null);
  const videoStreamRef  = useRef(null);
  const frameCaptureRef = useRef(null);
  const framesRef       = useRef([]);           // base64 frames, max 5

  // Helper: keep both React state (for renders) and ref (for closures) in sync
  function updateTranscript(val) {
    transcriptRef.current = val;
    setTranscript(val);
  }

  // ── Unlock AudioContext during user gesture ───────────────────────────────
  function unlockAudio() {
    if (!ttsCtxRef.current) {
      try { const ctx = new (window.AudioContext || window.webkitAudioContext)(); ctx.resume(); ttsCtxRef.current = ctx; } catch (_) {}
    } else { ttsCtxRef.current.resume().catch(() => {}); }
  }

  // Timer
  useEffect(() => {
    if (phase !== "session") return;
    timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  // Re-attach video stream when session phase mounts the <video> element.
  // startVideoStream() runs during "loading" when videoRef.current may be null,
  // so we must assign srcObject once the element is in the DOM.
  useEffect(() => {
    if (phase === "session" && videoStreamRef.current) {
      // The video element may render slightly after phase changes — retry briefly
      function attach() {
        if (videoRef.current && videoStreamRef.current) {
          videoRef.current.srcObject = videoStreamRef.current;
          return true;
        }
        return false;
      }
      if (!attach()) {
        const retryId = setInterval(() => { if (attach()) clearInterval(retryId); }, 100);
        const timeoutId = setTimeout(() => clearInterval(retryId), 2000);
        return () => { clearInterval(retryId); clearTimeout(timeoutId); };
      }
    }
  }, [phase]);

  // Mic wave
  useEffect(() => {
    if (!isRecording) { setAudioWave([0,0,0,0,0,0,0]); return; }
    const iv = setInterval(() => setAudioWave([...Array(7)].map(() => Math.random() * 32 + 4)), 80);
    return () => clearInterval(iv);
  }, [isRecording]);

  // ── TTS playback ───────────────────────────────────────────────────────────
  async function playInterviewerAudio(rawText) {
    if (finishedRef.current || playingRef.current) return;
    playingRef.current = true;
    const text  = cleanText(rawText);
    const words = text.split(" ").filter(Boolean);

    setPhase(p => p === "loading" ? "session" : p);
    if (words.length === 0) { playingRef.current = false; return; }

    if (stopTimerRef.current) { stopTimerRef.current(); stopTimerRef.current = null; }
    if (currentSrcRef.current) { try { currentSrcRef.current.stop(); } catch (_) {} currentSrcRef.current = null; }

    setSubtitleWords(words);
    setVisibleWordCount(0);
    setIsPlayingAudio(true);

    try {
      if (finishedRef.current) return;
      const profile = INTERVIEWER_PROFILES.find(p => p.key === session.interviewer_profile);
      const buf = await fetchTTS(text, profile?.voiceId);
      if (finishedRef.current) return; // user clicked "Encerrar" while TTS was loading
      if (buf.byteLength < 500) throw new Error(`Áudio inválido (${buf.byteLength} bytes)`);

      let ctx = ttsCtxRef.current;
      if (!ctx || ctx.state === "closed") { ctx = new (window.AudioContext || window.webkitAudioContext)(); ttsCtxRef.current = ctx; }
      if (ctx.state === "suspended") await ctx.resume();

      const audioBuf = await new Promise((res, rej) => ctx.decodeAudioData(buf.slice(0), res, rej));
      if (finishedRef.current) return; // check again after decode

      stopTimerRef.current = startWordTimer(words.length, audioBuf.duration * 1000,
        i => setVisibleWordCount(i), () => setVisibleWordCount(words.length));

      await new Promise(resolve => {
        const src = ctx.createBufferSource();
        src.buffer = audioBuf;
        src.connect(ctx.destination);
        currentSrcRef.current = src;
        src.onended = () => {
          if (stopTimerRef.current) { stopTimerRef.current(); stopTimerRef.current = null; }
          if (!finishedRef.current) setVisibleWordCount(words.length);
          currentSrcRef.current = null;
          resolve();
        };
        src.start(0);
      });
    } catch (err) {
      if (finishedRef.current) return;
      console.error("[TTS]", err);
      Sentry.captureException(err, { tags: { feature: "interview" } });
      setErrorMsg(err.message);
      if (stopTimerRef.current) { stopTimerRef.current(); stopTimerRef.current = null; }
      setVisibleWordCount(words.length);
    } finally {
      playingRef.current = false;
      if (finishedRef.current) return; // do NOT restart voice or update state after finish
      setIsPlayingAudio(false);
      setTimeout(() => { if (!finishedRef.current && !playingRef.current && !sendingRef.current) startVoice(); }, 400);
    }
  }

  // ── Video stream ───────────────────────────────────────────────────────────
  async function startVideoStream() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 } });
      videoStreamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setVideoGranted(true);

      // Capture a frame every 30s (max 5 frames)
      frameCaptureRef.current = setInterval(() => {
        if (framesRef.current.length >= 5) return;
        const frame = captureVideoFrame(videoRef.current);
        if (frame) framesRef.current.push(frame);
      }, 30000);

      // Capture first frame after 3s (to let camera warm up)
      setTimeout(() => {
        const frame = captureVideoFrame(videoRef.current);
        if (frame) framesRef.current.push(frame);
      }, 3000);

    } catch (e) {
      console.warn("[Camera]", e.message);
      setVideoGranted(false);
    }
  }

  function stopVideoStream() {
    clearInterval(frameCaptureRef.current);
    videoStreamRef.current?.getTracks().forEach(t => t.stop());
    videoStreamRef.current = null;
  }

  // ── Interview flow ─────────────────────────────────────────────────────────
  async function startInterview() {
    unlockAudio();
    setPhase("loading");
    // Start video stream alongside — don't block interview if it fails
    startVideoStream();
    await sendMessage(null, true);
  }

  async function sendMessage(text, isStart = false) {
    if (finishedRef.current || sendingRef.current) return;
    sendingRef.current = true;
    // Clear any pending auto-send silence timer to prevent double-send
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
    if (!isStart) unlockAudio();
    const userText = text || inputText.trim();
    if (!isStart && !userText) { sendingRef.current = false; return; }

    if (recognitionRef.current) { shouldBeRecordingRef.current = false; recognitionRef.current.stop(); setIsRecording(false); }

    let capturedMetrics = null;
    if (!isStart && voiceDataRef.current.volumes.length > 0) {
      const vols   = voiceDataRef.current.volumes;
      const avgVol = vols.reduce((a, b) => a + b, 0) / vols.length;
      capturedMetrics = {
        avg_volume: Math.round(avgVol), max_volume: Math.round(Math.max(...vols)),
        speech_duration: voiceDataRef.current.speechDuration,
        pause_count: voiceDataRef.current.pauses.length,
        energy:     avgVol > 60 ? "alta" : avgVol > 35 ? "média" : "baixa",
        confidence: avgVol > 55 && voiceDataRef.current.pauses.length < 3 ? "alta" : avgVol > 35 ? "média" : "baixa",
      };
      voiceDataRef.current = { volumes: [], pauses: [], speechDuration: 0 };
    }

    // Always read from ref — stays current even in stale closures (auto-send timer)
    let newTranscript = Array.isArray(transcriptRef.current) ? transcriptRef.current : [];
    if (!isStart && userText) {
      const userMsg = { role: "candidate", text: userText, timestamp: elapsed, voice_metrics: capturedMetrics };
      newTranscript = [...newTranscript, userMsg];
      updateTranscript(newTranscript);
      setLastCandidateText(userText);
      setInputText("");
      // Capture frame when candidate responds
      const frame = captureVideoFrame(videoRef.current);
      if (frame && framesRef.current.length < 5) framesRef.current.push(frame);
    }

    setLoading(true);
    setErrorMsg(null);
    setSubtitleWords([]);
    setVisibleWordCount(0);

    try {
      const res = await db.functions.invoke("interviewAI", {
        session_id: session.id, transcript: newTranscript, is_start: isStart,
      });
      const data = res?.data ?? res;
      const rawMessage = typeof data === 'string' ? data : data?.message;
      if (!rawMessage) throw new Error("Resposta vazia da IA.");

      // Track turn info for countdown display
      if (data?.turnInfo) setTurnInfo(data.turnInfo);

      const aiMsg      = { role: "interviewer", text: rawMessage, timestamp: elapsed };
      const updated    = [...newTranscript, aiMsg];
      updateTranscript(updated);
      setLoading(false);

      // Save to DB in background — a network hiccup here must not cancel TTS or break the interview
      db.entities.InterviewSession.update(session.id, { transcript: updated })
        .catch(err => console.warn("[DB sync]", err));
      await playInterviewerAudio(rawMessage);

      // Auto-end: if the AI signaled the interview is over, finish after TTS completes
      if (data?.shouldEnd && !finishedRef.current) {
        finishInterview();
      }
    } catch (err) {
      console.error("[interviewAI]", err);
      Sentry.captureException(err, { tags: { feature: "interview" } });
      setErrorMsg(err?.message || "Erro ao conectar com a IA. Tente novamente.");
      setLoading(false);
      setPhase(p => p === "loading" ? "session" : p);
    } finally {
      sendingRef.current = false;
    }
  }

  // ── Voice recognition ──────────────────────────────────────────────────────
  function startVoice() {
    if (finishedRef.current || playingRef.current || sendingRef.current) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    const recog = new SR();
    recog.lang = "pt-BR";
    recog.interimResults = true;
    recog.continuous = true; // keep mic open across natural pauses

    latestTextRef.current = "";
    shouldBeRecordingRef.current = true;

    recog.onresult = e => {
      const text = Array.from(e.results).map(r => r[0].transcript).join("");
      setInputText(text);
      latestTextRef.current = text;

      // Reset 3-second silence timer — fires auto-send when user stops speaking
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      if (text.trim()) {
        silenceTimerRef.current = setTimeout(() => {
          silenceTimerRef.current = null;
          const textToSend = latestTextRef.current.trim();
          if (textToSend && recognitionRef.current) {
            shouldBeRecordingRef.current = false; // intentional stop for auto-send
            recognitionRef.current.stop();
            setTimeout(() => sendMessage(textToSend), 80);
          }
        }, 3000);
      }
    };

    recog.onend = () => {
      setIsRecording(false);
      stopMicAnalysis();
      // Auto-restart if Chrome killed the session unexpectedly (network timeout, tab throttle, etc.)
      if (shouldBeRecordingRef.current) {
        setTimeout(() => { if (shouldBeRecordingRef.current) startVoice(); }, 500);
      }
    };

    recog.onerror = e => { if (e.error !== "no-speech" && e.error !== "aborted") console.warn("[Speech]", e.error); };

    recog.start();
    recognitionRef.current = recog;
    setIsRecording(true);
    startMicAnalysis();
  }

  function stopVoice() {
    shouldBeRecordingRef.current = false;
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
    recognitionRef.current?.stop();
    setIsRecording(false);
    stopMicAnalysis();
  }

  async function startMicAnalysis() {
    try {
      // Reuse the mic stream acquired at session start — avoids repeated getUserMedia calls
      if (!micStreamRef.current || micStreamRef.current.getTracks().every(t => t.readyState === "ended")) {
        micStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      const stream = micStreamRef.current;
      const ctx    = new AudioContext();
      micCtxRef.current = ctx;
      const source   = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      const data        = new Uint8Array(analyser.frequencyBinCount);
      let speechStart   = Date.now(), silenceStart = null;
      function tick() {
        if (!analyserRef.current) return;
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        voiceDataRef.current.volumes.push(avg);
        if (avg > 20) { if (silenceStart) { const d = Date.now() - silenceStart; if (d > 400) voiceDataRef.current.pauses.push(d); silenceStart = null; } }
        else { if (!silenceStart) silenceStart = Date.now(); }
        voiceDataRef.current.speechDuration = (Date.now() - speechStart) / 1000;
        requestAnimationFrame(tick);
      }
      tick();
    } catch (e) { console.warn("Mic analysis failed:", e); }
  }

  function stopMicAnalysis() {
    // Stop the analyser loop and AudioContext, but keep the stream alive for reuse next turn
    analyserRef.current = null;
    micCtxRef.current?.close().catch(() => {});
    micCtxRef.current = null;
  }

  async function finishInterview() {
    if (finishedRef.current) return; // already finishing
    finishedRef.current = true; // block all further async work (TTS, mic, sendMessage)
    playingRef.current = false;
    sendingRef.current = false;
    shouldBeRecordingRef.current = false; // prevent any auto-restart after session ends
    // Capture final frame before stopping
    const finalFrame = captureVideoFrame(videoRef.current);
    if (finalFrame && framesRef.current.length < 5) framesRef.current.push(finalFrame);

    clearInterval(timerRef.current);
    if (recognitionRef.current) { recognitionRef.current.abort?.(); recognitionRef.current = null; }
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
    if (stopTimerRef.current) stopTimerRef.current();
    stopMicAnalysis();
    // Now fully release the mic stream (kept alive during session for reuse)
    micStreamRef.current?.getTracks().forEach(t => t.stop());
    micStreamRef.current = null;
    stopVideoStream();
    if (currentSrcRef.current) { try { currentSrcRef.current.stop(); } catch (_) {} }
    ttsCtxRef.current?.close().catch(() => {});

    const safeT = Array.isArray(transcriptRef.current) ? transcriptRef.current : [];
    // DB save in background — never block the transition to analysis
    db.entities.InterviewSession.update(session.id, {
      status: "analyzing", transcript: safeT, duration_seconds: elapsed,
    }).catch(err => console.warn("[DB finishInterview]", err));

    onFinish(safeT, elapsed, framesRef.current);
  }

  // ── Phase: loading ─────────────────────────────────────────────────────────
  if (phase === "loading") {
    return (
      <div className="max-w-xl mx-auto py-24 flex flex-col items-center gap-8">
        <div className="relative flex items-center justify-center">
          <div className="absolute w-36 h-36 rounded-full animate-ping" style={{ background: "rgba(0,220,164,0.10)" }} />
          <div className="absolute w-28 h-28 rounded-full animate-ping" style={{ background: "rgba(0,220,164,0.15)", animationDelay: "0.4s" }} />
          <div className="relative w-24 h-24 rounded-full flex items-center justify-center"
            style={{ background: "linear-gradient(160deg, #003326 0%, #005040 100%)" }}>
            <User className="w-10 h-10" style={{ color: "#00DCA4" }} />
          </div>
        </div>
        <div className="text-center">
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
            Carregando sua entrevista...
          </h2>
          <p className="text-sm" style={{ color: "#646C6F" }}>
            Já vamos começar. O entrevistador está se preparando para te receber.
          </p>
        </div>
        <div className="flex gap-2.5">
          {[0,1,2].map(i => (
            <motion.div key={i} className="w-2.5 h-2.5 rounded-full" style={{ background: "#00DCA4" }}
              animate={{ y: [0,-10,0], opacity: [0.5,1,0.5] }}
              transition={{ duration: 0.9, delay: i * 0.25, repeat: Infinity }} />
          ))}
        </div>
      </div>
    );
  }

  // ── Phase: ready ───────────────────────────────────────────────────────────
  if (phase === "ready") {
    return (
      <div className="max-w-xl mx-auto text-center space-y-6 py-12">
        <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center"
          style={{ background: "linear-gradient(135deg, #00DCA4, #00664C)" }}>
          <Mic className="w-8 h-8 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
            Tudo pronto para começar
          </h2>
          <p className="text-sm" style={{ color: "#646C6F" }}>
            O entrevistador vai falar com você. Responda por voz — o microfone abre automaticamente após cada pergunta.
          </p>
        </div>
        <div className="rounded-2xl p-4 text-left space-y-2" style={{ background: "#EAFAF6", border: "1px solid #B0FCEA" }}>
          <p className="text-xs font-bold" style={{ color: "#00664C" }}>Configuração:</p>
          <p className="text-sm" style={{ color: "#003326" }}>
            <strong>{session.interview_type}</strong> · {session.interviewer_profile}
          </p>
          <p className="text-sm" style={{ color: "#003326" }}>Vaga: <strong>{session.job_title}</strong> ({session.seniority})</p>
        </div>
        <button onClick={startInterview}
          className="w-full py-4 rounded-full text-sm font-bold"
          style={{ background: "#00DCA4", color: "#003326" }}>
          Iniciar entrevista
        </button>
      </div>
    );
  }

  // ── Phase: session ─────────────────────────────────────────────────────────
  const visibleText = subtitleWords.slice(0, visibleWordCount).join(" ");
  const hiddenText  = subtitleWords.slice(visibleWordCount).join(" ");

  return (
    <div className="max-w-2xl mx-auto flex flex-col gap-4" style={{ minHeight: "calc(100vh - 180px)" }}>

      {/* Status bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#00DCA4" }} />
          <span className="text-xs font-semibold" style={{ color: "#00664C" }}>
            {loading ? "Processando..." : isPlayingAudio ? "Entrevistador falando" : isRecording ? "Ouvindo você" : "Aguardando resposta"}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {videoGranted && (
            <div className="flex items-center gap-1">
              <Camera className="w-3 h-3" style={{ color: "#00DCA4" }} />
              <span className="text-xs" style={{ color: "#00664C" }}>{framesRef.current.length}/5</span>
            </div>
          )}
          {turnInfo && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{
              background: turnInfo.current >= turnInfo.total - 1 ? "#FEF3C7" : "#EAFAF6",
              border: `1px solid ${turnInfo.current >= turnInfo.total - 1 ? "#F59E0B" : "#B0FCEA"}`,
            }}>
              <span className="text-xs font-bold" style={{
                color: turnInfo.current >= turnInfo.total - 1 ? "#92400E" : "#00664C",
              }}>
                Pergunta {turnInfo.current}/{turnInfo.total}
              </span>
            </div>
          )}
          <button onClick={finishInterview}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: "#FADBDF", color: "#892E38" }}>
            <Square className="w-3 h-3" /> Encerrar
          </button>
        </div>
      </div>

      {/* Interviewer panel */}
      <div className="flex-1 rounded-3xl p-8 flex flex-col items-center justify-center gap-6 relative overflow-hidden"
        style={{ background: "linear-gradient(160deg, #003326 0%, #005040 100%)", minHeight: 360 }}>

        {/* User video — always mounted so ref is stable; hidden when no permission */}
        <div className="absolute bottom-4 right-4 flex flex-col items-center gap-1"
          style={{ display: videoGranted ? "flex" : "none" }}>
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{
              width: 128, height: 96,
              borderRadius: 12,
              objectFit: "cover",
              transform: "scaleX(-1)",
              border: "2px solid rgba(0,220,164,0.35)",
              background: "#000",
            }}
          />
          <span className="text-xs font-medium" style={{ color: "rgba(0,220,164,0.6)" }}>Você</span>
        </div>

        {/* Avatar */}
        <div className="relative">
          <div className="w-20 h-20 rounded-full flex items-center justify-center"
            style={{ background: isPlayingAudio ? "#00DCA4" : "#004838", transition: "background 0.4s" }}>
            <User className="w-9 h-9" style={{ color: isPlayingAudio ? "#003326" : "#00DCA4" }} />
          </div>
          {isPlayingAudio && (
            <>
              <div className="absolute inset-0 rounded-full animate-ping" style={{ background: "rgba(0,220,164,0.2)" }} />
              <div className="absolute -inset-3 rounded-full animate-ping" style={{ background: "rgba(0,220,164,0.1)", animationDelay: "0.3s" }} />
            </>
          )}
        </div>

        <div className="text-center w-full">
          <p className="text-xs font-semibold mb-3" style={{ color: "#00DCA4" }}>
            {INTERVIEWER_PROFILES.find(p => p.key === session.interviewer_profile)?.name || session.interviewer_profile}
          </p>
          <div className="max-w-md mx-auto min-h-[96px] flex items-center justify-center px-2">
            {loading ? (
              <div className="flex gap-1.5 items-center">
                {[0,1,2].map(i => (
                  <motion.div key={i} className="w-2 h-2 rounded-full" style={{ background: "#00DCA4" }}
                    animate={{ y: [0,-6,0] }} transition={{ duration: 0.5, delay: i * 0.15, repeat: Infinity }} />
                ))}
              </div>
            ) : subtitleWords.length > 0 ? (
              <p className="text-lg font-medium leading-relaxed text-center" style={{ color: "#FFFFFF" }}>
                <span>{visibleText}</span>
                {hiddenText && <span style={{ color: "transparent" }}>{" " + hiddenText}</span>}
              </p>
            ) : (
              <p className="text-sm" style={{ color: "#6B8F80" }}>
                {isRecording ? "Ouvindo sua resposta…" : "Aguardando…"}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Candidate response */}
      <AnimatePresence>
        {lastCandidateText && !isRecording && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="px-4 py-3 rounded-2xl" style={{ background: "#EAFAF6", border: "1px solid #B0FCEA" }}>
            <p className="text-xs font-semibold mb-0.5" style={{ color: "#00664C" }}>Você disse:</p>
            <p className="text-sm" style={{ color: "#003326" }}>{lastCandidateText}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {errorMsg && (
        <div className="px-4 py-3 rounded-xl text-sm" style={{ background: "#FADBDF", color: "#892E38" }}>⚠️ {errorMsg}</div>
      )}

      {/* Controls */}
      <div className="space-y-2">
        {isRecording && (
          <div className="flex items-end justify-center gap-1 h-8">
            {audioWave.map((h, i) => (
              <div key={i} className="w-1.5 rounded-full transition-all duration-75"
                style={{ height: Math.max(4, h), background: "#00DCA4" }} />
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <button onClick={isRecording ? stopVoice : startVoice} disabled={isPlayingAudio || loading}
            className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
            style={isRecording
              ? { background: "#FADBDF", color: "#892E38" }
              : isPlayingAudio || loading ? { background: "#F2F4F4", color: "#C4C9CB" }
              : { background: "#EAFAF6", color: "#00664C" }}>
            {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>
          <input value={inputText} onChange={e => setInputText(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && !loading && sendMessage()}
            placeholder={isRecording ? "Fala detectada…" : "Ou escreva sua resposta…"}
            disabled={isPlayingAudio || loading}
            className="flex-1 px-4 py-3 rounded-2xl border text-sm"
            style={{ borderColor: "#E5E9EA", background: isPlayingAudio || loading ? "#F9FAFA" : "#fff" }} />
          <button onClick={() => sendMessage()} disabled={!inputText.trim() || loading || isPlayingAudio}
            className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
            style={inputText.trim() && !loading && !isPlayingAudio
              ? { background: "#00DCA4", color: "#003326" }
              : { background: "#E5E9EA", color: "#99A6AB" }}>
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
